import { postTicket } from "@/api/support";
import Button from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { DialogClose } from "@radix-ui/react-dialog";
import { Check, Image, Loader, X } from "lucide-react";
import { useTheme } from "next-themes";
import { useEffect, useState } from "react";
import GlassButton from "./GlassButton";
import ToastModal from "./ToastModal";

export default function SupportTicket({ modalOpen, setModalOpen }) {
  const landingPage = import.meta.env.VITE_LANDING_PAGE;

  const { setTheme } = useTheme();

  useEffect(() => {
    if (
      landingPage === "enfinity" ||
      landingPage === "task" ||
      landingPage === "vvv"
    ) {
      setTheme("dark");
    } else {
      setTheme("light");
    }
  }, [landingPage, setTheme]);

  const subjects = [
    "Account Creation",
    "Account & Login Issues",
    "Technical Bugs",
    "General Inquiry",
  ];

  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [email, setEmail] = useState("");
  const [incidentDate, setIncidentDate] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [successToast, setSuccessToast] = useState(false);
  const [toastType, setToastType] = useState(false);

  const handleSubmit = async () => {
    setIsSending(true);

    try {
      const payload = new FormData();
      payload.append("topic", subject);
      payload.append("description", description);
      payload.append("email", email);
      payload.append("incident_date", incidentDate);
      payload.append("dashboard_name", "enfinipay");
      payload.append("support_title", "Ticket");

      if (attachedFiles.length > 0) {
        payload.append("attachment", attachedFiles[0].file);
      }
      const response = await postTicket(payload);
      if (response) {
        setModalOpen(false);
        setToastType("success");
        setSuccessToast(true);
      }
    } catch (error) {
      console.error("Error submitting ticket:", error);
      setToastType("failed");
      setSuccessToast(true);
    } finally {
      setIsSending(false);
    }
  };

  const [attachedFiles, setAttachedFiles] = useState([]);
  const MAX_FILE_SIZE_MB = 2;
  const ACCEPTED_FILE_TYPES = "image/jpeg, image/png";

  const handleFileChange = (event) => {
    const file = event.target.files[0];

    if (!file) return;

    if (file.size / (1024 * 1024) <= MAX_FILE_SIZE_MB) {
      setAttachedFiles([
        {
          name: file.name,
          size: (file.size / (1024 * 1024)).toFixed(1),
          file: file,
        },
      ]);
    } else {
      alert(`File "${file.name}" exceeds the ${MAX_FILE_SIZE_MB}MB limit.`);
    }
  };

  const handleRemoveFile = (fileName) => {
    setAttachedFiles((prevFiles) =>
      prevFiles.filter((file) => file.name !== fileName)
    );
  };

  return (
    <>
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="rounded-[20px] max-w-xs md:max-w-xl text-center bg-white dark:bg-[#161616] text-black dark:text-white overflow-hidden border-0">
          <div className="space-y-5 text-left">
            <div className="space-y-1 px-1">
              <h1 className="text-xl font-bold dark:font-medium">
                Submit a{" "}
                <span className="text-primary dark:text-white">
                  Support Ticket
                </span>
              </h1>
              <p className="text-sm pr-6">
                Please fill out a ticket letting us know about your incident.
                Our support team will review and respond within 3-7 days.
              </p>
            </div>

            <div className="space-y-4 max-h-[60vh] overflow-auto pb-2 px-2">
              <div className="space-y-2">
                <h2 className="font-semibold text-base">Subject</h2>
                <Select onValueChange={setSubject}>
                  <SelectTrigger className="w-full rounded-lg py-5 dark:border-gray-400 focus-visible:ring-1 focus-visible:ring-primary">
                    <SelectValue placeholder="Select a topic" />
                  </SelectTrigger>
                  <SelectContent className="bg-white dark:bg-[#161616] border-primary dark:border-white/80 p-2">
                    {subjects.map((subjectItem) => (
                      <SelectItem
                        key={subjectItem}
                        value={subjectItem}
                        className="hover:bg-gray-200 hover:dark:text-black rounded-md"
                      >
                        {subjectItem}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <h2 className="font-semibold text-base">Description</h2>
                <Textarea
                  placeholder="Describe the issues you are experiencing"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="dark:border-gray-400 focus-visible:ring-1 focus-visible:ring-primary"
                />
              </div>
              <div className="space-y-2">
                <h2 className="font-semibold text-base">Email</h2>
                <Input
                  type="email"
                  placeholder="Email"
                  className="py-5 rounded-lg dark:border-gray-400 focus-visible:ring-1 focus-visible:ring-primary"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <h2 className="font-semibold text-base">Incident Date</h2>
                <Input
                  type="date"
                  className="py-5 rounded-lg uppercase flex justify-between dark:border-gray-400 focus-visible:ring-1 focus-visible:ring-primary"
                  value={incidentDate}
                  onChange={(e) => setIncidentDate(e.target.value)}
                />
              </div>
              <div className="space-y-4">
                {" "}
                <h2 className="font-semibold text-base">Attachments</h2>
                <div className="flex flex-col md:flex-row items-center gap-4">
                  <label
                    htmlFor="file-upload"
                    className="px-6 py-[7px] border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer dark:border dark:border-[#DDDDDD] dark:bg-transparent dark:text-white/50 text-nowrap"
                  >
                    BROWSE FILES
                  </label>
                  <input
                    id="file-upload"
                    type="file"
                    accept={ACCEPTED_FILE_TYPES}
                    onChange={handleFileChange}
                    className="sr-only"
                    multiple={false}
                  />
                  <div className="text-xs text-gray-500 dark:text-white/50">
                    <p>Files must be {MAX_FILE_SIZE_MB}MB or smaller.</p>
                    <p>
                      Accepted file types:{" "}
                      {ACCEPTED_FILE_TYPES.split(",")
                        .map((type) => type.trim())
                        .join(", ")}
                      .
                    </p>
                  </div>
                </div>
                <div className="space-y-2">
                  {attachedFiles.map((file, index) => (
                    <>
                      <Separator />
                      <div
                        key={file.name + index}
                        className="flex items-center justify-between pt-2"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-11 h-11 flex items-center justify-center border border-gray-300 rounded-md p-">
                            <Image className="text-primary dark:text-white/80" />
                          </div>
                          <div className="text-black dark:text-white/80">
                            <p className="text-sm font-medium">{file.name}</p>
                            <p className="text-sm">{file.size} mb</p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleRemoveFile(file.name)}
                          className="flex gap-1 items-center text-red-500 hover:text-red-400 dark:text-white/80 dark:hover:text-white text-sm font-medium"
                        >
                          <X className="h-4 w-4" />
                          Remove
                        </button>
                      </div>
                    </>
                  ))}
                </div>
              </div>
              <Separator />
              <p className="font-medium">
                Please do not include any personal or sensitive information.
              </p>
            </div>

            <div className="flex gap-3 pr-2">
              <DialogClose asChild>
                <Button className="rounded-xl bg-black text-white hover:bg-black dark:bg-black dark:text-white hover:dark:bg-black py-5 w-1/2">
                  Cancel
                  <X />
                </Button>
              </DialogClose>
              {landingPage === "enfinity" ? (
                <GlassButton
                  className="!py-[9.2px] w-1/2"
                  restrict={
                    isSending ||
                    !subject | !description ||
                    !email ||
                    !incidentDate
                  }
                  action={() => {
                    handleSubmit();
                  }}
                  name={
                    isSending ? (
                      <>
                        <Loader className="animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        Submit Ticket
                        <Check />
                      </>
                    )
                  }
                />
              ) : (
                <Button
                  className="bg-primary text-white rounded-xl py-5 w-1/2"
                  disabled={
                    isSending ||
                    !subject | !description ||
                    !email ||
                    !incidentDate
                  }
                  onClick={() => {
                    handleSubmit();
                  }}
                >
                  {isSending ? (
                    <>
                      <Loader className="animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      Submit Ticket
                      <Check />
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <ToastModal
        showToast={successToast}
        setShowToast={setSuccessToast}
        form="support"
        type={toastType}
      />
    </>
  );
}
